import React, {useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import InputMask from 'react-input-mask';

import {UserContext} from '../../UserContext';
import {useHttpClient} from '../hooks/useHttpClient';
import {useNotificacoes} from '../hooks/useNotificacoes';

// Validações e formatações
import {formatarPercentual,} from '../../util/validacoes';

import styles from './CadastroUsuario.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import Formulario from '../../components/ui/Formulario';
import {usePreventDoubleClick} from '../hooks/usePreventDoubleClick';
import {useDebounce} from '../hooks/useDebounce';
import {useFormularioComReset} from '../hooks/useFormularioComReset';

function Section({title, children}) {
    return (
        <section className={styles.section}>
            <div className={styles.sectionHeader}>
                <h3>{title}</h3>
                <div className={styles.sectionLine}/>
            </div>
            {children}
        </section>
    );
}

function DetalharUsuario() {
    const {id} = useParams();
    const navigate = useNavigate();
    const {user} = useContext(UserContext);
    const {get, post, put} = useHttpClient();
    const {exibirErro} = useNotificacoes();

    const [formData, setFormData, handleChange] = useFormularioComReset(
        {
            idEmpresa: user?.empresa?.id || '',
            idUsina: '',
            idTipoUsuario: '',
            idTipoPessoa: '',
            nome: '',
            unidadeNome: '',
            cpfCnpj: '',
            telefone: '',
            email: '',
            saldoCredito: '',
            endereco: '',
            complementoEndereco: '',
            municipio: '',
            uf: '',
            cep: '',
            codCliente: '',
            bairro: '',
            diaVencimentoFatura: '',
            senhaDistribuidora: '',
            retPis: '',
            retCofins: '',
            retCsll: '',
            retIrrf: '',
            percentualDesconto: '',
            ehCondominio: '',
            cadastroPendente: false,
            origemCadastro: ''
        },
        ['idTipoPessoa']
    );

    const [usinaSelecionada, setUsinaSelecionada] = useState(null);
    const [tipoUsinaId, setTipoUsinaId] = useState(null); // 2 = Consórcio
    const [showSenha, setShowSenha] = useState(false);


    const [percentualBruto, setPercentualBruto] = useState('');
    const [percentualFormatado, setPercentualFormatado] = useState('');

    const [empresas, setEmpresas] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [tiposUsuario, setTiposUsuario] = useState([]);
    const [tiposPessoa, setTiposPessoa] = useState([]);
    const [cpfCnpjMask, setCpfCnpjMask] = useState('999.999.999-99');
    const [dataLoaded, setDataLoaded] = useState(false);

    const cepDebounced = useDebounce(formData.cep, 600);
    const diasVenc = Array.from({length: 31}, (_, i) => i + 1);

    // --- dados auxiliares
    const fetchUsinas = async (empresaId) => {
        if (!empresaId) return;
        try {
            const data = await get(`/hello-sol/usinas/buscar?idEmpresa=${empresaId}`);
            setUsinas(data || []);
        } catch (err) {
            exibirErro('Erro ao buscar usinas: ' + err.message);
        }
    };

    const carregarUsuario = async () => {
        if (!id) {
            await fetchUsinas(user?.empresa?.id);
            setDataLoaded(true);
            return;
        }
        try {
            const data = await get(`/hello-sol/usuarios/${id}`);

            setCpfCnpjMask(data.cpfCnpj?.length === 11 ? '999.999.999-99' : '99.999.999/9999-99');

            const pNum = parseFloat(String(data.percentualDesconto ?? '0').toString().replace(',', '.')) || 0;
            setPercentualBruto(pNum.toFixed(2));
            setPercentualFormatado(formatarPercentual(pNum.toFixed(2)));

            const formattedSaldo =
                data.saldoCredito != null ? Number(data.saldoCredito).toFixed(5).replace('.', ',') : '';

            setFormData({
                ...data,
                saldoCredito: formattedSaldo
            });

            await fetchUsinas(data.idEmpresa);

            if (data.idUsina) {
                const detalhe = await get(`/hello-sol/usinas/${data.idUsina}`);
                setUsinaSelecionada(detalhe);
                setTipoUsinaId(String(detalhe.idTipoUsina));
            }

            setDataLoaded(true);
        } catch (err) {
            exibirErro('Erro ao carregar dados do usuário: ' + err.message);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const empresaId = user?.empresa?.id;
                setEmpresas((await get(`/hello-sol/empresas/lista/${empresaId}`)) || []);
                setTiposUsuario((await get(`/hello-sol/tipos-usuario`)) || []);
                setTiposPessoa((await get(`/hello-sol/tipos-pessoa`)) || []);
                await carregarUsuario();
            } catch (err) {
                exibirErro(err.message);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // CEP -> auto-preenchimento
    useEffect(() => {
        if (id == null && cepDebounced?.length === 9) {
            fetch(`https://viacep.com.br/ws/${cepDebounced.replace('-', '')}/json/`)
                .then((r) => r.json())
                .then((d) => {
                    if (d.erro) {
                        exibirErro('CEP não encontrado. Preencha o endereço manualmente.');
                        setFormData((prev) => ({
                            ...prev,
                            endereco: '',
                            bairro: '',
                            municipio: '',
                            uf: ''
                        }));
                    } else {
                        setFormData((prev) => ({
                            ...prev,
                            endereco: d.logradouro,
                            bairro: d.bairro,
                            municipio: d.localidade,
                            uf: d.uf
                        }));
                    }
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cepDebounced]);

    // ao selecionar usina carregamos tipo e só então mostramos a seção da distribuidora
    const onChangeUsina = async (e) => {
        const value = e.target.value;
        handleChange(e);

        // limpamos a seção até sabermos o tipo
        setTipoUsinaId(null);
        setUsinaSelecionada(null);
        setFormData((prev) => ({...prev, retPis: '', retCofins: '', retCsll: '', retIrrf: ''}));

        if (!value) return;

        try {
            const detalhe = await get(`/hello-sol/usinas/${value}`);
            setUsinaSelecionada(detalhe);
            setTipoUsinaId(String(detalhe.idTipoUsina));

            // se for consórcio, preenche defaults
            if (String(detalhe.idTipoUsina) === '2') {
                setFormData((prev) => ({
                    ...prev,
                    retPis: prev.retPis || '0,65',
                    retCofins: prev.retCofins || '3,00',
                    retCsll: prev.retCsll || '1,00',
                    retIrrf: prev.retIrrf || '1,50'
                }));
            }
        } catch (err) {
            exibirErro('Não foi possível carregar a usina selecionada.');
        }
    };

    // percentual
    const onChangePercentual = (e) => {
        const raw = e.target.value.replace(/[^\d,\.]/g, '').replace(',', '.');
        const num = parseFloat(raw);
        if (!isNaN(num)) {
            const limitado = Math.min(num, 100);
            setPercentualBruto(limitado.toFixed(2));
            setPercentualFormatado(e.target.value);
            setFormData((f) => ({...f, percentualDesconto: limitado}));
        } else {
            setPercentualBruto('');
            setPercentualFormatado('');
            setFormData((f) => ({...f, percentualDesconto: null}));
        }
    };
    const onBlurPercentual = () => {
        if (percentualBruto !== '') setPercentualFormatado(formatarPercentual(parseFloat(percentualBruto)));
    };

    const handleTipoPessoaChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value, cpfCnpj: ''}));
        setCpfCnpjMask(value === '1' ? '999.999.999-99' : '99.999.999/9999-99');
    };

    const toNum = (s) => (s ? parseFloat(String(s).replace(',', '.')) : null);

    // validações
    const validateForm = () => {
        const cnpjPattern = /^(?:\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}|\d{14})$/;
        const cpfPattern = /^(?:\d{3}\.\d{3}\.\d{3}-\d{2}|\d{11})$/;
        const phonePattern = /^\(\d{2}\) \d{4,5}-\d{4}$/;
        const cepPattern = /^\d{5}-\d{3}$/;

        if (!dataLoaded) return exibirErro('Dados ainda estão carregando.');
        if (!cpfPattern.test(formData.cpfCnpj) && !cnpjPattern.test(formData.cpfCnpj))
            return exibirErro('CPF/CNPJ inválido.');
        if (!phonePattern.test(formData.telefone)) return exibirErro('Telefone inválido.');
        if (!cepPattern.test(formData.cep)) return exibirErro('CEP inválido.');

        // Cliente precisa selecionar a Usina para liberar a seção da distribuidora
        if (String(formData.idTipoUsuario) === '3') {
            if (!formData.idUsina) {
                exibirErro('Selecione a Usina para continuar o cadastro do Cliente.');
                return false;
            }

            if (!formData.senhaDistribuidora || formData.senhaDistribuidora.trim() === '') {
                exibirErro('Informe a senha da distribuidora.');
                return false;
            }

            if (String(tipoUsinaId) === '2') {
                const asNum = (s) => parseFloat(String(s || '').replace(',', '.'));
                const pis = asNum(formData.retPis);
                const cof = asNum(formData.retCofins);
                const cs = asNum(formData.retCsll);
                const ir = asNum(formData.retIrrf);

                if ([pis, cof, cs].some((n) => Number.isNaN(n))) {
                    exibirErro('Preencha PIS, COFINS e CSLL corretamente.');
                    return false;
                }
                // quando condomínio, IRRF pode ser 0 (ou vazio -> será 0 no back)
                if (!formData.ehCondominio && Number.isNaN(ir)) {
                    exibirErro('Preencha o IRRF corretamente.');
                    return false;
                }
            }
        }
        return true;
    };

    const salvarUsuario = async () => {
        if (!validateForm()) return;

        const cleanedSaldo = String(formData.saldoCredito || '')
            .replace(/[^\d,\.]/g, '')
            .replace(/\./g, '')
            .replace(',', '.');
        const saldoNumero = parseFloat(cleanedSaldo) || 0;

        const body = {
            ...formData,
            saldoCredito: saldoNumero,
            percentualDesconto: percentualBruto ? parseFloat(percentualBruto) : null,
            retPis: toNum(formData.retPis),
            retCofins: toNum(formData.retCofins),
            retCsll: toNum(formData.retCsll),
            retIrrf: formData.ehCondominio ? 0 : toNum(formData.retIrrf),
        };

        try {
            if (id) await put(`/hello-sol/usuarios/${id}`, body);
            else await post(`/hello-sol/usuarios`, body);
            navigate('/home/listar-usuario');
        } catch (error) {
            exibirErro(error.message || 'Erro ao salvar usuário.');
        }
    };

    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarUsuario);

    return (
        <div className={styles_global.container}>
            <h2>Detalhar Usuário</h2>
            {formData.cadastroPendente && (
                <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '8px', background: '#fff3cd', color: '#8a5a00' }}>
                    Cadastro pendente de revisão.
                    {formData.origemCadastro ? ` Origem: ${formData.origemCadastro}.` : ''}
                </div>
            )}

            <form>

                {/* Seção 1: vínculo e tipo */}
                <Section title="Dados do Usuário">
                    <div className={styles.linhaUsuario}>
                        <div className={styles_content.formField}>
                            <label htmlFor="idTipoUsuario">Tipo de Usuário</label>
                            <select
                                name="idTipoUsuario"
                                value={formData.idTipoUsuario}
                                onChange={handleChange}
                                id="idTipoUsuario"
                                disabled
                            >
                                <option value="">Selecionar Tipo de Usuário</option>
                                {tiposUsuario.map((tipoUsuario) => (
                                    <option key={tipoUsuario.id} value={tipoUsuario.id}>
                                        {tipoUsuario.descricao}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles_content.formField}>
                            <label htmlFor="idEmpresa">Empresa</label>
                            <select
                                name="idEmpresa"
                                value={formData.idEmpresa}
                                onChange={handleChange}
                                id="idEmpresa"
                                disabled
                            >
                                <option value="">Selecionar Empresa</option>
                                {empresas.map((empresa) => (
                                    <option key={empresa.id} value={empresa.id}>
                                        {empresa.nome}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {String(formData.idTipoUsuario) === '3' && (
                            <div className={styles_content.formField}>
                                <label htmlFor="idUsina">Usina</label>
                                <select
                                    name="idUsina"
                                    value={formData.idUsina}
                                    onChange={onChangeUsina}
                                    id="idUsina"
                                    disabled
                                >
                                    <option value="">Selecionar Usina</option>
                                    {usinas.map((usina) => (
                                        <option key={usina.id} value={usina.id}>
                                            {usina.nome}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {String(formData.idTipoUsuario) === '3' && (
                        <div className={styles.linhaUsuario}>
                            <div className={styles_content.formField}>
                                <label htmlFor="codCliente">Código do Cliente</label>
                                <input
                                    type="text"
                                    name="codCliente"
                                    value={formData.codCliente}
                                    onChange={handleChange}
                                    id="codCliente"
                                    maxLength={15}
                                    disabled
                                />
                            </div>

                            <div className={styles_content.formField}>
                                <label htmlFor="percentualDesconto">Percentual de Desconto</label>
                                <input
                                    id="percentualDesconto"
                                    name="percentualDesconto"
                                    value={percentualFormatado}
                                    placeholder="0,00 %"
                                    maxLength={8}
                                    disabled
                                    onChange={onChangePercentual}
                                    onBlur={onBlurPercentual}
                                />
                            </div>

                            <div className={styles_content.formField}>
                                <label htmlFor="diaVencimentoFatura">Dia do Vencimento da Fatura</label>
                                <select
                                    id="diaVencimentoFatura"
                                    name="diaVencimentoFatura"
                                    value={String(formData.diaVencimentoFatura ?? '')}
                                    onChange={handleChange}
                                    disabled
                                >
                                    <option value="">Selecione (1–31)</option>
                                    {diasVenc.map((d) => (
                                        <option key={d} value={d}>
                                            {d}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </Section>

                {/* Seção 2: dados pessoais */}
                <div className={styles.linhaUsuario}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idTipoPessoa">Tipo de Pessoa</label>
                        <select
                            name="idTipoPessoa"
                            value={formData.idTipoPessoa}
                            onChange={handleTipoPessoaChange}
                            id="idTipoPessoa"
                            disabled
                        >
                            <option value="">Selecione o Tipo de Pessoa</option>
                            {tiposPessoa.map((tipo) => (
                                <option key={tipo.id} value={tipo.id}>
                                    {tipo.descricao}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="cpfCnpj">CPF/CNPJ</label>
                        <InputMask
                            mask={cpfCnpjMask}
                            maskChar=""
                            type="text"
                            name="cpfCnpj"
                            value={formData.cpfCnpj}
                            onChange={handleChange}
                            id="cpfCnpj"
                            disabled
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="nome">Nome</label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleChange}
                            id="nome"
                            disabled
                            maxLength={100}
                        />
                    </div>
                </div>

                <div className={styles.linhaUsuario}>
                    <div className={styles_content.formField}>
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            id="email"
                            disabled
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="telefone">Telefone</label>
                        <InputMask
                            mask="(99) 99999-9999"
                            maskChar=""
                            type="text"
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleChange}
                            id="telefone"
                            disabled
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="cep">CEP</label>
                        <InputMask
                            mask="99999-999"
                            maskChar=""
                            type="text"
                            name="cep"
                            value={formData.cep}
                            onChange={(e) => setFormData((prev) => ({...prev, cep: e.target.value}))}
                            id="cep"
                            disabled
                        />
                    </div>
                </div>

                <div className={styles.linhaUsuario}>
                    <div className={styles_content.formField}>
                        <label htmlFor="endereco">Endereço</label>
                        <input
                            type="text"
                            name="endereco"
                            value={formData.endereco}
                            onChange={handleChange}
                            id="endereco"
                            disabled
                            maxLength={100}
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="complementoEndereco">Complemento (opcional)</label>
                        <input
                            type="text"
                            name="complementoEndereco"
                            value={formData.complementoEndereco}
                            onChange={handleChange}
                            id="complementoEndereco"
                            maxLength={50}
                            disabled
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="bairro">Bairro</label>
                        <input
                            type="text"
                            name="bairro"
                            value={formData.bairro}
                            onChange={handleChange}
                            id="bairro"
                            disabled
                            maxLength={50}
                        />
                    </div>
                </div>

                <div className={styles.linhaUsuario}>
                    <div className={styles_content.formField}>
                        <label htmlFor="uf">UF</label>
                        <input
                            type="text"
                            name="uf"
                            value={formData.uf}
                            onChange={handleChange}
                            id="uf"
                            disabled
                            maxLength={2}
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="municipio">Município</label>
                        <input
                            type="text"
                            name="municipio"
                            value={formData.municipio}
                            onChange={handleChange}
                            id="municipio"
                            disabled
                            maxLength={50}
                        />
                    </div>
                </div>

                {/* Consórcio → retenções + flag condomínio */}
                {String(tipoUsinaId) === '2' && (
                    <Section title="Taxas para Compensação (Consórcio)">
                        <div className={styles.linhaUsuario}>
                            <div className={styles_content.formField}>
                                <label>Cliente é Condomínio</label>
                                <div className={styles.inlineRadios}>
                                    <label className={styles.radioInline}>
                                        <input
                                            type="radio"
                                            name="ehCondominio"
                                            value="sim"
                                            checked={!!formData.ehCondominio}
                                            onChange={() => {
                                                setFormData(prev => ({...prev, retIrrf: '0,00'})); // default condomínio
                                            }}
                                            disabled
                                        />
                                        <span>Sim</span>
                                    </label>
                                    <label className={styles.radioInline}>
                                        <input
                                            type="radio"
                                            name="ehCondominio"
                                            value="nao"
                                            checked={!formData.ehCondominio}
                                            onChange={() => {
                                                setFormData(prev => ({...prev, retIrrf: ''})); // limpa IRRF
                                            }}
                                            disabled
                                        />
                                        <span>Não</span>
                                    </label>
                                </div>
                            </div>

                            <div className={styles_content.formField}>
                                <label htmlFor="retPis">PIS (%) </label>
                                <input
                                    type="text"
                                    name="retPis"
                                    id="retPis"
                                    value={formData.retPis}
                                    onChange={handleChange}
                                    placeholder="0,65"
                                    disabled
                                />
                            </div>
                            <div className={styles_content.formField}>
                                <label htmlFor="retCofins">COFINS (%) </label>
                                <input
                                    type="text"
                                    name="retCofins"
                                    id="retCofins"
                                    value={formData.retCofins}
                                    onChange={handleChange}
                                    placeholder="3,00"
                                    disabled
                                />
                            </div>
                            <div className={styles_content.formField}>
                                <label htmlFor="retCsll">CSLL (%) </label>
                                <input
                                    type="text"
                                    name="retCsll"
                                    id="retCsll"
                                    value={formData.retCsll}
                                    onChange={handleChange}
                                    placeholder="1,00"
                                    disabled
                                />
                            </div>
                            <div className={styles_content.formField}>
                                <label htmlFor="retIrrf">IRRF (%) {formData.ehCondominio ? '(não aplicável)' : '*'}</label>
                                <input
                                    type="text"
                                    name="retIrrf"
                                    id="retIrrf"
                                    value={formData.retIrrf}
                                    onChange={handleChange}
                                    placeholder={formData.ehCondominio ? '0,00' : '1,50'}
                                    disabled
                                />
                            </div>
                        </div>
                    </Section>
                )}

                {/* Seção 3: conta na distribuidora – só aparece após selecionar a usina */}
                {String(formData.idTipoUsuario) === '3' && formData.idUsina && (
                    <Section title="Informações de Acesso à Distribuidora">
                        {/* ...dentro da <Section title="Informações de Acesso à Distribuidora"> */}
                        <div className={styles.linhaUsuario}>
                            <div className={styles_content.formField}>
                                <label htmlFor="senhaDistribuidora">Senha da Distribuidora</label>

                                {/* wrapper relativo que não quebra o alinhamento */}
                                <div className={styles.passwordContainer}>
                                    <input
                                        type={showSenha ? 'text' : 'password'}
                                        name="SenhaDistribuidoraFixToAvoidAutocomplete"
                                        id="senhaDistribuidora"
                                        value={formData.senhaDistribuidora}
                                        onChange={(e) => setFormData(p => ({
                                            ...p,
                                            senhaDistribuidora: e.target.value
                                        }))}
                                        autoComplete="new-password"
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    </Section>
                )}

                {/* Botão de voltar */}
                <div className={styles_content.buttonGroup}>
                    {formData.cadastroPendente && (
                        <>
                            <button
                                type="button"
                                onClick={() => navigate(`/home/cadastrar-usuario/${id}`)}>
                                Regularizar Mesmo Cadastro
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate(`/home/revisar-cliente-pendente/${id}`)}>
                                Revincular para Cliente Existente
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate('/home/listar-usuario')}>
                        Voltar
                    </button>
                </div>
            </form>
        </div>
    );
}

export default DetalharUsuario;

